import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

type ThemeMode = 'light' | 'dark';

interface ThemeContextType {
  mode: ThemeMode;
  toggleMode: () => void;
  setMode: (mode: ThemeMode) => void;
  /**
   * Whether the dark view is on offer. Everything that shows a control for it -
   * the header toggle, the row on the cookie settings page - asks this first,
   * so the offer disappears in one move rather than being switched off in one
   * place and left advertised in another.
   */
  darkModeEnabled: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const STORAGE_KEY = 'themeMode';
const DARK_QUERY = '(prefers-color-scheme: dark)';

const isThemeMode = (value: unknown): value is ThemeMode => value === 'light' || value === 'dark';

/** Only persist the choice once the visitor has allowed it - see the cookie policy. */
const mayPersist = (): boolean => {
  try {
    if (localStorage.getItem('cookieConsent') !== 'accepted') return false;
    const preferences = localStorage.getItem('cookiePreferences');
    return preferences ? JSON.parse(preferences).theme === true : false;
  } catch {
    return false;
  }
};

/**
 * A remembered choice only counts while consent still covers it - a value left
 * over from an earlier visit must not outrank the operating system setting.
 */
const readStoredMode = (): ThemeMode | null => {
  try {
    if (!mayPersist()) return null;
    const saved = localStorage.getItem(STORAGE_KEY);
    return isThemeMode(saved) ? saved : null;
  } catch {
    return null;
  }
};

/**
 * Light or dark, in this order of precedence:
 *   0. light, and nothing else, while the site does not offer the dark view,
 *   1. a choice the visitor made and allowed us to remember,
 *   2. the operating system setting, followed live while no choice is stored,
 *   3. light.
 *
 * Nothing is written to storage unless cookie consent covers it, so a visitor
 * who declined still gets the system setting - it just is not remembered.
 *
 * `darkModeEnabled` comes from the content rather than from here, because it is
 * an editorial decision: see ContentUseCase.isDarkModeEnabled. Switched off, a
 * stored choice and the system setting are both left unread rather than
 * overwritten - so whatever a visitor picked while the dark view was on offer
 * is still theirs if it is offered again.
 */
export const ThemeProvider: React.FC<{ children: ReactNode; darkModeEnabled: boolean }> = ({
  children,
  darkModeEnabled,
}) => {
  // Starts light so the server-rendered markup and the first paint agree; the
  // effect below corrects it before the browser paints.
  const [mode, setModeState] = useState<ThemeMode>('light');

  useEffect(() => {
    if (!darkModeEnabled) {
      setModeState('light');
      return;
    }

    const stored = readStoredMode();
    if (stored) {
      setModeState(stored);
      return;
    }

    const mediaQuery = window.matchMedia(DARK_QUERY);
    setModeState(mediaQuery.matches ? 'dark' : 'light');

    // Keep following the system until the visitor picks something themselves.
    const handleChange = (event: MediaQueryListEvent) => {
      if (!readStoredMode()) {
        setModeState(event.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [darkModeEnabled]);

  const setMode = useCallback(
    (newMode: ThemeMode) => {
      // Nothing on the page can reach this while the dark view is off - the
      // controls are gone. The guard is for the case where one is missed, so a
      // forgotten button cannot put the site into a view it does not support.
      if (!darkModeEnabled) return;

      setModeState(newMode);
      try {
        if (mayPersist()) {
          localStorage.setItem(STORAGE_KEY, newMode);
        } else {
          localStorage.removeItem(STORAGE_KEY);
        }
      } catch {
        // Storage unavailable - the choice still applies for this visit.
      }
    },
    [darkModeEnabled]
  );

  const toggleMode = useCallback(
    () => setMode(mode === 'light' ? 'dark' : 'light'),
    [mode, setMode]
  );

  return (
    <ThemeContext.Provider value={{ mode, toggleMode, setMode, darkModeEnabled }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useThemeMode = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useThemeMode must be used within a ThemeProvider');
  }
  return context;
};
